import React, { useEffect, useId } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Skeleton,
} from "@mui/material";

import { ReactComponent as MenuBookIcon } from "../../../../assets/images/folder.svg";
import { useSelector } from "react-redux";
import { loadGoogleFont, useScopedFontStyle } from "../../../../utils";




const CategoryGrid = ({
  categories = [],
  title = "Browse by Category",
  marginTop = "100px",
  onCategoryClick,
  loading = false,
  skeletonCount = 6,
}) => {
  const { data: kbSettings, initialized } = useSelector((state) => state.externalKbSettings);
  const accentColor = kbSettings?.data?.accent_color;
  const fontFamily = kbSettings?.data?.font_family || "Inter";

  const rawId = useId();
  const scopeId = rawId.replace(/:/g, "");

  useEffect(() => {
    loadGoogleFont(fontFamily);
  }, [fontFamily]);

  useScopedFontStyle(scopeId, fontFamily);

  // Loading skeleton
  if (loading || !initialized) {
    return (
      <Box
        data-kb-font-id={scopeId}
        sx={{ width: "100%", marginTop: { xs: "22px", sm: "32px", md: "32px", lg: marginTop } }}
      >
        <Skeleton variant="text" width="40%" height={50} sx={{ mb: 4 }} />

        <Grid
          sx={{ marginTop: { xs: "20px", sm: "30px", md: "30px", lg: "33px" } }}
          container
          spacing={{ xs: 2, sm: 3, md: 3, lg: 4 }}
        >
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{
                flexBasis: { xs: "100%", sm: "50%", md: "33.333333%" },
                maxWidth: { xs: "100%", sm: "50%", md: "33.333333%" },
              }}
            >
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "none",
                  backgroundColor: "#FBFCFC",
                  padding: { xs: "20px", lg: "30px" },
                }}
              >
                <Skeleton variant="circular" width={44} height={44} sx={{ mb: 2 }} />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="60%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!categories || categories.length === 0) return null;

  return (
    <Box
      data-kb-font-id={scopeId}
      sx={{ width: "100%", marginTop: { xs: "22px", sm: "30px", md: "30px", lg: marginTop } }}
    >
      <Typography
        sx={{
          color: "#0C141B",
          fontSize: { xs: "28px", sm: "36px", lg: "44px" },
          fontWeight: 700,
          lineHeight: "normal",
          marginBottom: { xs: "20px", sm: "30px", md: "30px", lg: "33px" },
        }}
      >
        {title}
      </Typography>

      <Grid
        container
        spacing={{ xs: "20px", sm: "20px", md: "30px", lg: "40px" }}
      >
        {categories.map((category, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={4}
            key={category._id || index}
            sx={{
              WebkitTapHighlightColor: "transparent", // 🔥 remove mobile blue
            }}
          >
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                backgroundColor: "#FBFCFC",
                padding: { xs: "20px", lg: "30px" },
                cursor: "pointer",

                // 🔥 REMOVE BLUE CLICK / FOCUS EFFECT
                outline: "none",
                WebkitTapHighlightColor: "transparent",

                "&:focus": {
                  outline: "none",
                },
                "&:focus-visible": {
                  outline: "none",
                },
                "&:active": {
                  backgroundColor: "#FBFCFC",
                },
              }}
              onClick={() => onCategoryClick?.(category)}
            >
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: "white",
                  "& svg": { width: "100%", height: "100%" },
                }}
              >
                <MenuBookIcon />
              </Avatar>

              <CardContent
                sx={{
                  flexGrow: 1,
                  paddingLeft: "0px",
                  "&:last-child": { paddingBottom: 0 },
                }}
              >
                <Typography
                  sx={{
                    color: "#0C141B",
                    fontSize: { xs: "20px", lg: "24px" },
                    fontWeight: 700,
                    lineHeight: { xs: "28px", lg: "34px" },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {category.name || "Unnamed Category"}
                </Typography>

                <Typography
                  sx={{
                    color: accentColor || "#148DFD",
                    fontSize: "14px",
                    fontWeight: 600,
                    marginTop: "24px",
                    lineHeight: "24px",
                    textTransform: "uppercase",
                  }}
                >
                  {(category.articleCount || 0)} ARTICLES
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default CategoryGrid;